import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { FEATURE_CARD_ACCENT_WIDTH } from "@shell/styles/designTokens";

import { CloudProvider, InfraType } from "../../types";

interface Props {
  cloudProvider: CloudProvider | null;
  infraType: InfraType | null;
  onCloudProviderChange: (provider: CloudProvider) => void;
  onInfraTypeChange: (infra: InfraType) => void;
}

interface ProviderOption {
  provider: CloudProvider;
  label: string;
  description: string;
  storageLabel: string;
  freeTierNote?: string;
}

interface InfraOption {
  infra: InfraType;
  label: string;
  description: string;
}

const PROVIDER_OPTIONS: ProviderOption[] = [
  {
    provider: CloudProvider.AWS,
    label: "Amazon Web Services",
    description: "Deploy on AWS infrastructure",
    storageLabel: "S3 · ECS · RDS · Glue · Kinesis · Lambda",
  },
  {
    provider: CloudProvider.GCP,
    label: "Google Cloud Platform",
    description: "Deploy on GCP infrastructure",
    storageLabel: "GCS · Cloud Run · BigQuery · Dataproc · Pub/Sub",
  },
  {
    provider: CloudProvider.AZURE,
    label: "Microsoft Azure",
    description: "Deploy on Azure infrastructure",
    storageLabel: "ADLS · App Service · Synapse · Cosmos DB · Event Hubs",
  },
  {
    provider: CloudProvider.SELF_HOSTED,
    label: "Self-hosted",
    description: "Deploy on your own servers or bare metal",
    storageLabel: "MinIO · k3s / RKE2 · PostgreSQL · Redis",
  },
  {
    provider: CloudProvider.RUSHA_HOSTED,
    label: "Rusha Hosted",
    description: "Fully managed by Rusha — zero infra to configure",
    storageLabel: "Managed K8s · Auto-scaling · Included storage",
    freeTierNote:
      "Counts toward free tier stack limit. Paid billing is metered per CPU/RAM hour.",
  },
];

/** Providers where infraType is predetermined — no sub-picker shown */
const AUTO_INFRA_TYPE: Partial<Record<CloudProvider, InfraType>> = {
  [CloudProvider.RUSHA_HOSTED]: "rusha_hosted",
};

const INFRA_OPTIONS_BY_PROVIDER: Record<CloudProvider, InfraOption[]> = {
  [CloudProvider.AWS]: [
    {
      infra: "aws_services",
      label: "AWS Managed Services",
      description:
        "Use AWS-native managed services: ECS Fargate, Lambda, RDS, Glue ETL, Kinesis, MSK, Athena, Redshift. No cluster management — pay for what you use.",
    },
    {
      infra: "aws_eks",
      label: "AWS EKS (Kubernetes)",
      description:
        "Run containerised workloads on Amazon Elastic Kubernetes Service. Full K8s API with AWS-managed control plane. Ideal if you already use Kubernetes.",
    },
  ],
  [CloudProvider.GCP]: [
    {
      infra: "gcp_services",
      label: "GCP Managed Services",
      description:
        "Use GCP-native managed services: Cloud Run, BigQuery, Dataproc, Dataflow, Pub/Sub, Cloud SQL. Serverless-first with automatic scaling.",
    },
    {
      infra: "gcp_gke",
      label: "GCP GKE (Kubernetes)",
      description:
        "Run containerised workloads on Google Kubernetes Engine — the most mature managed K8s offering with Autopilot and Standard modes.",
    },
  ],
  [CloudProvider.AZURE]: [
    {
      infra: "azure_services",
      label: "Azure Managed Services",
      description:
        "Use Azure-native managed services: App Service, Synapse Analytics, ADLS, Cosmos DB, Event Hubs, Azure Data Factory. Deep enterprise integration.",
    },
    {
      infra: "azure_aks",
      label: "Azure AKS (Kubernetes)",
      description:
        "Run containerised workloads on Azure Kubernetes Service. Integrated with Azure AD, ACR, and Azure Monitor.",
    },
  ],
  [CloudProvider.SELF_HOSTED]: [
    {
      infra: "k8s_self_hosted",
      label: "Kubernetes (self-hosted)",
      description:
        "Run your own K8s cluster with k3s, RKE2, microk8s, or kubeadm on bare metal or on-premise VMs. Full orchestration with rolling deploys and auto-healing.",
    },
    {
      infra: "linux_server",
      label: "Linux Server / Standalone",
      description:
        "Deploy directly on a server or VM with Docker Compose or as standalone processes. Simpler operations — ideal when K8s overhead is not justified.",
    },
  ],
  [CloudProvider.RUSHA_HOSTED]: [
    {
      infra: "rusha_hosted",
      label: "Rusha Hosted",
      description:
        "Deploy into a Rusha-managed environment. Rusha handles infrastructure provisioning, scaling, and operations on your behalf.",
    },
  ],
};

export function StepChooseCloud({
  cloudProvider,
  infraType,
  onCloudProviderChange,
  onInfraTypeChange,
}: Props) {
  const theme = useTheme();
  const infraOptions = cloudProvider
    ? INFRA_OPTIONS_BY_PROVIDER[cloudProvider]
    : [];
  const isAutoInfra =
    cloudProvider != null && AUTO_INFRA_TYPE[cloudProvider] != null;

  function handleProviderClick(provider: CloudProvider) {
    onCloudProviderChange(provider);
    const auto = AUTO_INFRA_TYPE[provider];
    if (auto) onInfraTypeChange(auto);
  }

  const selectionCardSx = (selected: boolean) => ({
    cursor: "pointer",
    borderRadius: 1,
    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    borderTopWidth: selected ? FEATURE_CARD_ACCENT_WIDTH : 1,
    borderTopColor: selected
      ? theme.palette.primary.main
      : theme.palette.divider,
    bgcolor: selected
      ? alpha(theme.palette.primary.main, 0.03)
      : "background.paper",
    transition: "border-color 0.15s, background-color 0.15s, transform 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "translateY(-2px)",
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: theme.palette.primary.main,
      outlineOffset: 2,
    },
  });

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Choose cloud provider
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select where your stack will run, then choose the infrastructure style
        within that provider.
      </Typography>

      {/* Provider cards */}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {PROVIDER_OPTIONS.map((opt) => {
          const selected = cloudProvider === opt.provider;
          return (
            <Paper
              key={opt.provider}
              variant="outlined"
              role="button"
              tabIndex={0}
              aria-label={`Select ${opt.label}`}
              onClick={() => handleProviderClick(opt.provider)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleProviderClick(opt.provider);
                }
              }}
              sx={{
                width: { xs: "100%", sm: "calc(50% - 8px)" },
                p: 2,
                ...selectionCardSx(selected),
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {opt.label}
                </Typography>
                {opt.freeTierNote && (
                  <Tooltip title={opt.freeTierNote} placement="top" arrow>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 16, color: "text.disabled" }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {opt.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.storageLabel}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {/* Infra type sub-selection — hidden for providers with auto-determined infra type */}
      {cloudProvider && !isAutoInfra && !infraType && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select an infrastructure type below to continue.
        </Alert>
      )}
      {cloudProvider && !isAutoInfra && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Choose infrastructure type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How should your stack be deployed within{" "}
            <strong>
              {
                PROVIDER_OPTIONS.find((p) => p.provider === cloudProvider)
                  ?.label
              }
            </strong>
            ?
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            {infraOptions.map((opt) => {
              const selected = infraType === opt.infra;
              return (
                <Paper
                  key={opt.infra}
                  variant="outlined"
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${opt.label}`}
                  onClick={() => onInfraTypeChange(opt.infra)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onInfraTypeChange(opt.infra);
                    }
                  }}
                  sx={{ flex: 1, p: 2, ...selectionCardSx(selected) }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {opt.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.78rem" }}
                  >
                    {opt.description}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
}
